import { ChartSection01, ParticipantsSection01, ResultsSection01, ChartSection02, UsersSection02, ParticipantsSection02, CheckinsSection02, EventRecordsSummarySection02, RsvpRecordsSection02 } from "../../components/EventHistorySections.js";
import React, { useCallback, useEffect, useState, useContext, useRef, useMemo } from "react";
import axios from "axios";
import { Alert, Container, Button, Col, Card, Row, Modal, Badge } from "react-bootstrap";
import { confirmAlert } from "react-confirm-alert";
import { FormComponent } from "../../components/Register";
import { ShowConfirm } from "../../components/Alerts.js";
import Loader from "../../components/Loader.js";
import { parse, isValid } from "date-fns";
import NotyfContext from "../../contexts/NotyfContext.js";
import useLayout from "../../hooks/useLayout.js";
import useAuth from "../../hooks/useAuth.js";
import {
  getExcelFromObject,
  getCollectedParticipantsExportHeaders,
  getOriginLabel,
  getDefaultDateRangeInTimeZone,
  getSignatureFileUrl,
  downloadSignatureFile,
  normalizeRfidTag,
} from "../../assets/functions.js";
import MediaPreviewModal from "../../components/MediaPreviewModal.js";
import {
  canEditEventReports,
  canAccessEventRsvps,
  canManageEvent,
} from "../../utils/eventPermissions.js";
import { getDownloadFileName } from "../../utils/downloadFilename.js";
import { EventAnalyticsDashboard } from "../../components/EventAnalyticsDashboard.js";
import { EventVacancyReport } from "../../components/EventVacancyReport.js";
import ActivityReportsSection from "../../components/ActivityReportsSection.js";
import StreamingReportsSection from "../../components/StreamingReportsSection.js";
import EventParticipantRankingSection from "../../components/EventParticipantRankingSection.js";
import { UserAdditionalInfoSection } from "../../components/UserAdditionalInfoSection.js";
import {
  setHeaders01,
  setHeadersResult,
  processData01,
} from "../../components/EventHistory01.js";
import {
  setHeaders02,
  setHeadersUsers,
  processData02,
  ModalCollectedParticipants,
} from "../../components/EventHistory02.js";

// Helper function to get default date range (14 days before and 14 days after today)
const getDefaultDateRangeMonth = () => {
  return getDefaultDateRangeInTimeZone(14, 14);
};

const getDefaultCheckinDateRange = () => {
  return getDefaultDateRangeInTimeZone(7, 7);
};

const REPORT_SECTION_NAMES = ["analytics", "ranking", "activities", "streamings"];

const getSingleApplicationId = (applications) => {
  if (!Array.isArray(applications) || applications.length !== 1) {
    return "";
  }

  const applicationId = applications[0]?.customizacao_id;
  return applicationId === undefined || applicationId === null
    ? ""
    : String(applicationId);
};

const getFormFieldVacancyErrorMessage = (error) => {
  const responseData = error?.response?.data;
  if (responseData?.code === "FORM_FIELD_VACANCY_EXHAUSTED") {
    return responseData.message || "A opção selecionada não possui vagas disponíveis.";
  }
  if (responseData?.code === "INVALID_FORM_FIELD_VACANCY") {
    return responseData.message || "A configuração de vagas do evento é inválida.";
  }
  return null;
};

const runWithGlobalLoading = (setLoading, request) => {
  setLoading(true);
  return Promise.resolve()
    .then(request)
    .finally(() => setLoading(false));
};

const parseParticipantJson = (value) => {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return { ...value };
  }

  if (typeof value !== "string" || !value.trim()) {
    return {};
  }

  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? { ...parsed }
      : {};
  } catch (error) {
    return {};
  }
};

const getExistingParticipantJson = (participant, fields) => {
  const existingJson = parseParticipantJson(participant?.json);

  // The report rows normally expose the participant JSON as well as flattened
  // field values. Use the latter only as a fallback for older/incomplete rows.
  fields.forEach((field) => {
    const fieldKey = String(field.inputID);
    if (
      !Object.prototype.hasOwnProperty.call(existingJson, fieldKey)
      && participant
      && Object.prototype.hasOwnProperty.call(participant, field.inputID)
      && participant[field.inputID] !== undefined
      && participant[field.inputID] !== null
    ) {
      existingJson[fieldKey] = {
        label: field.label,
        value: participant[field.inputID],
      };
    }
  });

  return existingJson;
};

const buildParticipantJson = (values, fields, editParticipant = null) => {
  const isEditing = Boolean(editParticipant);
  const json = isEditing
    ? getExistingParticipantJson(editParticipant, fields)
    : {};

  Object.entries(values || {}).forEach(([fieldKey, value]) => {
    const field = fields.find((candidate) => String(candidate.inputID) === fieldKey);
    if (!field) return;

    // If a hidden-on-registration field was not present in the flattened edit
    // row, keep its persisted JSON value instead of replacing it with the
    // form's empty fallback. When the row contains the field, its current form
    // value is authoritative so the panel can still edit it.
    if (
      isEditing
      && field.hideFieldOnRegister
      && Object.prototype.hasOwnProperty.call(json, fieldKey)
      && !Object.prototype.hasOwnProperty.call(editParticipant, field.inputID)
    ) {
      return;
    }

    const normalizedValue = (field.rfigtag === true || field.validation === 'rfid')
      ? normalizeRfidTag(value)
      : value;

    json[fieldKey] = { label: field.label, value: normalizedValue };
  });

  return json;
};

export const EventHistory01 = ({
  dataTable: initialDataTable,
  updateDataTable,
  setDataTableUpdate,
}) => {
  const [localTotalParticipantes, setLocalTotalParticipantes] = useState(
    initialDataTable?.total_participantes || 0
  );

  useEffect(() => {
    setLocalTotalParticipantes(initialDataTable?.total_participantes || 0);
  }, [initialDataTable?.total_participantes]);

  const dataTable = { ...initialDataTable, total_participantes: localTotalParticipantes };

  const [historyData, setHistoryData] = useState(false);
  const [chartEntries, setChartEntries] = useState([]);
  const [updateDataTableWarning, setDataTableUpdateWarning] = useState(false);
  const [triggerExport, setTriggerExport] = useState(false);
  const [triggerExportResult, setTriggerExportResult] = useState(false);
  const [resetTrigger, setResetTrigger] = useState(0);
  const { setLoading } = useLayout();
  const notyf = useContext(NotyfContext);
  const singleApplicationId = getSingleApplicationId(initialDataTable?.aplicativos);
  const [filter, setFilter] = useState(() => ({
    extraPropsResults: null,
    extraPropsParticipants: false,
    customizacao_id: singleApplicationId,
    dateRange: getDefaultDateRangeMonth(),
  }));

  useEffect(() => {
    if (!singleApplicationId) return;

    setFilter((previousFilter) => (
      previousFilter.customizacao_id === singleApplicationId
        ? previousFilter
        : { ...previousFilter, customizacao_id: singleApplicationId }
    ));
  }, [singleApplicationId]);

  const [fields, setFields] = useState([]);
  const [fieldsResult, setFieldsResult] = useState([]);
  const formikRef = useRef(null);
  const [showImageModal, setShowImageModal] = useState({
    show: false,
    url: "",
    id: 0,
  });
  const [showEditModal, setShowEditModal] = useState({ show: false, data: {} });
  const isUpdating = useRef(false);
  const [filteredParticipants, setFilteredParticipants] = useState([]);
  const [zipIsReady, setZipIsReady] = useState(true);
  const userState = useAuth();
  const canEditReports = canEditEventReports(userState, dataTable.platform);
  const [isDownloadingReportImages, setIsDownloadingReportImages] = useState(false);
  const selectedPhotoReportApplication = dataTable.aplicativos?.find(
    (application) => parseInt(application.customizacao_id, 10) === parseInt(filter.customizacao_id, 10),
  );
  const canDownloadReportImages = canEditEventReports(userState, dataTable.platform) && [10, 11].includes(
    parseInt(selectedPhotoReportApplication?.aplicativo_id, 10),
  );
  const [totalCounts, setTotalCounts] = useState({
    participants: 0,
    results: 0,
  });

  const downloadPlatform1ReportImages = async () => {
    if (!canDownloadReportImages || isDownloadingReportImages) return;

    setIsDownloadingReportImages(true);
    const toastId = notyf.open({
      type: "success",
      message: "Gerando o ZIP das fotos do relatório...",
      ripple: true,
      dismissible: false,
      duration: 0,
    });

    try {
      const response = await axios.get(
        `/api/event/${dataTable.evento_id}/photo-report/${filter.customizacao_id}/images.zip`,
        { responseType: "blob" },
      );
      const downloadUrl = window.URL.createObjectURL(new Blob([response.data], { type: "application/zip" }));
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = getDownloadFileName(
        response.headers?.["content-disposition"],
        `relatorio-fotos-${dataTable.evento_id}-${filter.customizacao_id}.zip`,
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      notyf.dismiss(toastId);
      notyf.open({
        type: "success",
        message: "Download das fotos concluído.",
        ripple: true,
        dismissible: true,
      });
    } catch (error) {
      let message = "Não foi possível gerar o arquivo de fotos";
      if (error.response?.data instanceof Blob) {
        try {
          const body = JSON.parse(await error.response.data.text());
          message = body.message || message;
        } catch (parseError) {
          // Keep the generic error when the response body is not JSON.
        }
      } else {
        message = error.response?.data?.message || message;
      }
      notyf.dismiss(toastId);
      notyf.open({
        type: "danger",
        message,
        ripple: true,
        dismissible: true,
      });
    } finally {
      setIsDownloadingReportImages(false);
    }
  };

  function handleDelete(props, text) {
    if (!canEditReports) {
      return;
    }

    confirmAlert({
      closeOnEscape: false,
      closeOnClickOutside: false,
      customUI: ({ onClose }) => {
        return (
          <ShowConfirm
            title="Confirmar ação"
            description={text}
            action={() => {
              deleteHistory(props);
              onClose();
            }}
            onClose={onClose}
          />
        );
      },
    });
  }

  function deleteHistory(props) {
    if (!canEditReports) {
      return;
    }

    setLoading(true);
    let url = "";
    switch (props.type) {
      case "removeUsers":
        url = "/api/event/participants";
        break;
      case "removeUsersHistory":
        url = "/api/event/participants/history";
        break;
      case "removeUsersResults":
        url = "/api/event/result";
        break;

      default:
        setLoading(false);
        notyf.open({
          type: "danger",
          message: "Houve um erro, tente novamente",
          ripple: true,
          dismissible: true,
        });
        return;
    }
    axios
      .delete(url, { params: props })
      .then(function (response) {
        if (props.type === "removeUsers") {
          if (props.id || props.visitante_id) {
            setLocalTotalParticipantes((prev) => Math.max(0, prev - 1));
          } else {
            setLocalTotalParticipantes(0);
          }
        }
        notyf.open({
          type: "success",
          message: props.type === "resetAnalytics"
            ? "Novo período de medição analítica iniciado com sucesso"
            : "Exclusão realizada com sucesso",
          ripple: true,
          dismissible: true,
        });
        setDataTableUpdate((prevCount) => prevCount + 1);
      })
      .catch(function (error) {
        setLoading(false);
        notyf.open({
          type: "danger",
          message: "Houve um erro, tente novamente",
          ripple: true,
          dismissible: true,
        });
      });
  }

  const submitFormExternally = () => {
    if (canEditReports && formikRef.current) {
      formikRef.current.submitForm();
    }
  };

  const handleEdit = (data) => {
    if (!canEditReports) {
      return;
    }

    setShowEditModal({ show: true, data: data });
  };

  const handleNewUser = () => {
    if (!canEditReports) {
      return;
    }

    setShowEditModal({ show: true, data: {} });
  };

  const validateDate = (dateString, fieldName) => {
    if (!dateString) return { isValid: false, error: "Data é obrigatória" };
    try {
      const parsedDate = typeof dateString === "string"
        ? parse(dateString, "dd/MM/yyyy HH:mm", new Date())
        : dateString;
      if (!isValid(parsedDate)) return { isValid: false, error: "Formato de data inválido" };
      return { isValid: true, error: null };
    } catch {
      return { isValid: false, error: "Formato de data inválido" };
    }
  };

  const handleDateRangeChange = (type, date) => {
    setFilter((prev) => ({
      ...prev,
      dateRange: {
        ...prev.dateRange,
        [type]: date,
      },
    }));
  };

  function registerUser(values, actions) {
    if (!canEditReports) {
      actions?.setSubmitting(false);
      return;
    }

    let newArray = { ...values };
    let visitante_id = null;
    if (newArray) {
      if (newArray.hasOwnProperty("authorization")) {
        delete newArray.authorization;
      }
      if (newArray.visitante_id) {
        visitante_id = newArray.visitante_id;
      }
      delete newArray.visitante_id;
    }
    const json = JSON.stringify(buildParticipantJson(
      newArray,
      dataTable.json.form.fields,
      visitante_id ? showEditModal.data : null,
    ));

    if (visitante_id) {
      runWithGlobalLoading(setLoading, () =>
        axios.put(`/api/event/participants/${dataTable.evento_id}`, {
          visitante_id: visitante_id,
          platform: dataTable.platform,
          json: json,
        }),
      )
        .then(function (response) {
          setShowEditModal((prevState) => ({
            ...prevState,
            show: false,
          }));
          setDataTableUpdate((prevCount) => prevCount + 1);
          actions.setSubmitting(false);
        })
        .catch(function (error) {
          actions.setSubmitting(false);
          let errorMessage = "Houve um erro no registro do participante";
          const vacancyErrorMessage = getFormFieldVacancyErrorMessage(error);
          if (vacancyErrorMessage) {
            errorMessage = vacancyErrorMessage;
          }
          if (error.response?.status === 400 && error.response.data?.code === 4) {
            errorMessage = error.response.data.message || "E-mail não permitido para este evento";
          }
          notyf.open({
            type: "danger",
            message: errorMessage,
            ripple: true,
            dismissible: true,
          });
        });
    } else {
      const executeRegister = () => {
        runWithGlobalLoading(setLoading, () =>
          axios.post("/api/event/participants", {
            evento_id: dataTable.evento_id,
            platform: dataTable.platform,
            json: json,
          }),
        )
          .then(function (response) {
            setLocalTotalParticipantes((prev) => prev + 1);
            setShowEditModal((prevState) => ({
              ...prevState,
              show: false,
            }));
            setDataTableUpdate((prevCount) => prevCount + 1);
            actions.setSubmitting(false);
          })
          .catch(function (error) {
            console.error("DEBUG POST EVENTHISTORY01 ERROR:", error);
            actions.setSubmitting(false);
            let errorMessage = "Houve um erro no registro do participante";
            const vacancyErrorMessage = getFormFieldVacancyErrorMessage(error);
            if (vacancyErrorMessage) {
              errorMessage = vacancyErrorMessage;
            }
            if (error.response?.status === 400 && error.response.data?.code === 4) {
              errorMessage = error.response.data.message || "E-mail não permitido para este evento";
            }
            if (error.response?.status === 400 && error.response.data?.code === 3) {
              errorMessage = "Este e-mail já está registrado";
            }
            notyf.open({
              type: "danger",
              message: errorMessage,
              ripple: true,
              dismissible: true,
            });
          });
      };

      const formStatus = dataTable.json?.form?.status;
      const subscriptionLimitStatus = dataTable.json?.subscriptionLimit?.status;
      const subscriptionLimit = dataTable.json?.subscriptionLimit?.limit;
      const currentParticipantsCount = totalCounts?.participants || dataTable.total_participantes;

      console.log("--- DEBUG subscriptionLimit (EventHistory01) ---");
      console.log("dataTable.json?.subscriptionLimit:", dataTable.json?.subscriptionLimit);
      console.log("subscriptionLimitStatus:", subscriptionLimitStatus);
      console.log("subscriptionLimit:", subscriptionLimit);
      console.log("currentParticipantsCount:", currentParticipantsCount);

      const proceedWithLimitCheck = () => {
        if (
          subscriptionLimitStatus &&
          subscriptionLimit &&
          subscriptionLimit > 0 &&
          currentParticipantsCount >= subscriptionLimit
        ) {
          confirmAlert({
            closeOnEscape: false,
            closeOnClickOutside: false,
            customUI: ({ onClose }) => {
              return (
                <ShowConfirm
                  title="Confirmar ação"
                  description={`O limite de inscrições (${subscriptionLimit}) foi atingido. Deseja registrar o participante mesmo assim?`}
                  action={() => {
                    executeRegister();
                    onClose();
                  }}
                  onClose={() => {
                    actions.setSubmitting(false);
                    setLoading(false);
                    onClose();
                  }}
                />
              );
            },
          });
        } else {
          executeRegister();
        }
      };

      if (formStatus !== undefined && formStatus !== true && formStatus !== 'true') {
        confirmAlert({
          closeOnEscape: false,
          closeOnClickOutside: false,
          customUI: ({ onClose }) => {
            return (
              <ShowConfirm
                title="Confirmar ação"
                description="As inscrições para este evento estão fechadas. Deseja registrar o participante mesmo assim?"
                action={() => {
                  proceedWithLimitCheck();
                  onClose();
                }}
                onClose={() => {
                  actions.setSubmitting(false);
                  setLoading(false);
                  onClose();
                }}
              />
            );
          },
        });
      } else {
        proceedWithLimitCheck();
      }
    }
  }

  useEffect(() => {
    if (isUpdating.current === false) {
      isUpdating.current = true;
      if (historyData) {
        setLoading(true);
      }
      // eslint-disable-next-line eqeqeq
      let getIndexApp = dataTable.aplicativos.findIndex(
        (field) => parseInt(field.customizacao_id, 10) === parseInt(filter.customizacao_id, 10),
      );
      let applicationType =
        getIndexApp >= 0
          ? dataTable.aplicativos[getIndexApp].aplicativo_id
          : null;
      axios
        .get(
          `/api/event/participants/${dataTable.evento_id}?platform=${dataTable.platform}&customizacao_id=${filter.customizacao_id}&type=${applicationType}`,
        )
        .then(function (response) {
          setChartEntries(
            Array.isArray(response.data?.participants)
              ? response.data.participants
              : [],
          );
          let currentApp;
          if (applicationType !== null && dataTable.aplicativos[getIndexApp]) {
            currentApp = dataTable.aplicativos[getIndexApp];
          }
          let headers = setHeaders01(
            dataTable,
            handleDelete,
            filter,
            handleEdit,
            canEditReports,
          );
          setFields(headers);
          const isApp11 = parseInt(applicationType, 10) === 11;
          if (!isApp11 && response.data.results && response.data.results.length > 0) {
            setHeadersResult(setFieldsResult, handleDelete, canEditReports);
          } else {
            setFieldsResult([]);
          }
          let data = processData01(
            dataTable,
            response.data,
            currentApp,
            applicationType,
            setFields,
            setFieldsResult,
            setShowImageModal,
            filter,
          );

          setHistoryData(data);
          setTotalCounts({
            participants: data.totalParticipants || data.history.length,
            results: data.totalResults || data.results.length,
          });
          if (updateDataTableWarning === "update") {
            setDataTableUpdateWarning(false);
            notyf.open({
              type: "success",
              message: "Dados atualizados com sucesso",
              ripple: true,
              dismissible: true,
            });
          }
          setLoading(false);
          setTimeout(() => {
            isUpdating.current = false;
          }, 250);
        })
        .catch(function (error) {
          setLoading(false);
          notyf.open({
            type: "danger",
            message: "Houve um erro, tente novamente",
            ripple: true,
            dismissible: true,
          });
          setTimeout(() => {
            isUpdating.current = false;
          }, 250);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    updateDataTable,
    filter.customizacao_id,
    filter.dateRange?.startDate,
    filter.dateRange?.endDate,
    resetTrigger,
  ]);

  return (
    <Container fluid className="p-0">
      <MediaPreviewModal media={showImageModal} setMedia={setShowImageModal} />
      <ModalEdit01
        showEditModal={showEditModal}
        setShowEditModal={setShowEditModal}
        dataApplication={dataTable}
        formikRef={formikRef}
        registerUser={registerUser}
        submitFormExternally={submitFormExternally}
      ></ModalEdit01>
      {historyData === false || historyData === undefined ? (
        <div className="py-5">
          <Loader></Loader>
        </div>
      ) : (
        <>
          {/* Gráfico de registros - first */}
          <ChartSection01
            entries={chartEntries}
            dataTable={dataTable}
          />
          <EventVacancyReport dataTable={dataTable} />
          {/* Lista de participantes */}
          <ParticipantsSection01
            totalCounts={totalCounts}
            triggerExport={triggerExport}
            setTriggerExport={setTriggerExport}
            zipIsReady={zipIsReady}
            setZipIsReady={setZipIsReady}
            filteredParticipants={filteredParticipants}
            dataTable={dataTable}
            notyf={notyf}
            filter={filter}
            setFilter={setFilter}
            handleDelete={handleDelete}
            handleNewUser={handleNewUser}
            handleDateRangeChange={handleDateRangeChange}
            getDefaultDateRangeMonth={getDefaultDateRangeMonth}
            fields={fields}
            setFields={setFields}
            setFieldsResult={setFieldsResult}
            historyData={historyData}
            setFilteredParticipants={setFilteredParticipants}
             canEdit={canEditReports}
             canDownloadImages={canDownloadReportImages}
            isDownloadingImages={isDownloadingReportImages}
            onDownloadImages={downloadPlatform1ReportImages}
             resetTrigger={resetTrigger}
             setResetTrigger={setResetTrigger}
             />
          {/* Resultados */}
          <ResultsSection01
            totalCounts={totalCounts}
            triggerExportResult={triggerExportResult}
            setTriggerExportResult={setTriggerExportResult}
            filter={filter}
            setFilter={setFilter}
            dataTable={dataTable}
            handleDelete={handleDelete}
            handleDateRangeChange={handleDateRangeChange}
            getDefaultDateRangeMonth={getDefaultDateRangeMonth}
            fieldsResult={fieldsResult}
            historyData={historyData}

            canEdit={canEditReports}
            resetTrigger={resetTrigger}
            setResetTrigger={setResetTrigger}
          />
        </>
      )}
    </Container>
  );
};

export const EventHistory02 = ({
  dataTable: initialDataTable,
  updateDataTable,
  setDataTableUpdate,
  section = "reports",
}) => {
  const isRecordsSection = section === "records";
  const [localTotalParticipantes, setLocalTotalParticipantes] = useState(
    initialDataTable?.total_participantes || 0
  );

  useEffect(() => {
    setLocalTotalParticipantes(initialDataTable?.total_participantes || 0);
  }, [initialDataTable?.total_participantes]);

  const dataTable = useMemo(() => ({ ...initialDataTable, total_participantes: localTotalParticipantes }), [initialDataTable, localTotalParticipantes]);

  const [participantsResponse, setParticipantsResponse] = useState(null);
  const [recordsError, setRecordsError] = useState("");
  const [updateDataTableWarning, setDataTableUpdateWarning] = useState(false);
  const [triggerExport, setTriggerExport] = useState(false);
  const [triggerExportCheckins, setTriggerExportCheckins] = useState(false);
  const [triggerExportUsers, setTriggerExportUsers] = useState(false);
  const [resetTrigger, setResetTrigger] = useState(0);
  const { setLoading } = useLayout();
  const notyf = useContext(NotyfContext);
  const [filterParticipants, setFilterParticipants] = useState({
    extraPropsParticipants: false,
    dateRange: getDefaultDateRangeMonth(),
  });
  const [filterCheckins, setFilterCheckins] = useState({
    extraPropsParticipants: false,
    participantReportMode: "checkins",
    checkin_filter: 2,
    checkinDateRange: getDefaultCheckinDateRange(),
    registrationWindowMinutes: 10,
  });
  const rsvpSectionRef = useRef(null);
  const participantTableRef = useRef(null);
  const rsvpTableRef = useRef(null);
  const scrollToRecordTable = useCallback((target) => {
    const container = target?.closest(".main");
    if (!container) return;

    const targetTop = container.scrollTop + target.getBoundingClientRect().top
      - container.getBoundingClientRect().top - container.clientTop - 12;
    const maxScrollTop = Math.max(0, container.scrollHeight - container.clientHeight);
    container.scrollTo({
      top: Math.max(0, Math.min(targetTop, maxScrollTop)),
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "instant" : "smooth",
    });
  }, []);
  const [filterCredentialCharts, setFilterCredentialCharts] = useState({
    participantFieldFilter: null,
    participantValueFilter: null,
    dateRange: getDefaultDateRangeMonth(),
  });
  const [filterUsers, setFilterUsers] = useState({
    extraPropsUsers: false,
    dateRange: getDefaultDateRangeMonth(),
  });
  const [fields, setFields] = useState([]);
  const [fieldsCheckins, setFieldsCheckins] = useState([]);
  const [fieldsRsvp, setFieldsRsvp] = useState([]);
  const [fieldsUsers, setFieldsUsers] = useState([]);
  const historyData = useMemo(() => participantsResponse?.eventId === dataTable.evento_id
    ? processData02(dataTable, participantsResponse.data, filterParticipants, filterUsers, filterCredentialCharts, filterCheckins)
    : false, [dataTable, participantsResponse, filterParticipants, filterUsers, filterCredentialCharts, filterCheckins]);
  const chartEntries = useMemo(() => participantsResponse?.eventId === dataTable.evento_id
    ? (participantsResponse.data.participants || []).filter((participant) => Number(participant.type) === 0) : [], [dataTable.evento_id, participantsResponse]);
  const formikRef = useRef(null);
  const [showImageModal, setShowImageModal] = useState({
    show: false,
    url: "",
    id: 0,
  });
  const [showEditModal, setShowEditModal] = useState({
    show: false,
    type: 0,
    data: {},
  });
  const [showCollectedParticipantsModal, setShowCollectedParticipantsModal] =
    useState({ show: false });
  const [selectedUser, setSelectedUser] = useState({});
  const [zipIsReady, setZipIsReady] = useState(true);
  const [checkinsZipIsReady, setCheckinsZipIsReady] = useState(true);
  const [isDownloadingSignatureArchive, setIsDownloadingSignatureArchive] = useState(false);
  const requestSequenceRef = useRef(0);
  const userState = useAuth();
  // Report access enables report-page mutation controls; level 0 remains read-only.
  const canEditReports = canEditEventReports(userState, dataTable.platform);
  // Reset and synthetic usage tests are server-side management operations.
  const canManageAnalytics = canManageEvent(userState, dataTable.platform);
  const signatureEnabled = dataTable.json?.credentialAccess?.apresentation?.afterCheckin?.signature?.status === true;
  const [filteredParticipants, setFilteredParticipants] = useState([]);
  const [filteredCheckins, setFilteredCheckins] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [availableExhibitors, setAvailableExhibitors] = useState([]);
  const [loadingAvailableExhibitors, setLoadingAvailableExhibitors] =
    useState(false);
  const reportLoadingRef = useRef({ analytics: false, ranking: false, activities: false, streamings: false });
  const reportStartedRef = useRef(new Set());
  const reportHasLoadedOnceRef = useRef(false);
  const [reportsReady, setReportsReady] = useState(isRecordsSection);

  const handleReportLoadingChange = useCallback((sectionName, isLoading) => {
    if (isRecordsSection || !REPORT_SECTION_NAMES.includes(sectionName)) return;

    reportStartedRef.current.add(sectionName);
    if (isLoading) {
      if (reportHasLoadedOnceRef.current) setLoading(true);
    }

    const nextLoading = {
      ...reportLoadingRef.current,
      [sectionName]: isLoading,
    };
    reportLoadingRef.current = nextLoading;

    const allSectionsStarted = REPORT_SECTION_NAMES.every((name) => reportStartedRef.current.has(name));
    const allSectionsFinished = REPORT_SECTION_NAMES.every((name) => !nextLoading[name]);
    if (!allSectionsStarted || !allSectionsFinished) return;

    if (!reportHasLoadedOnceRef.current) {
      reportHasLoadedOnceRef.current = true;
      setReportsReady(true);
    } else {
      setLoading(false);
    }
  }, [isRecordsSection, setLoading]);

  useEffect(() => {
    if (isRecordsSection) {
      setReportsReady(true);
      return;
    }

    reportLoadingRef.current = { analytics: false, ranking: false, activities: false, streamings: false };
    reportStartedRef.current = new Set();
    reportHasLoadedOnceRef.current = false;
    setReportsReady(false);
  }, [isRecordsSection]);

  useEffect(() => {
    if (!isRecordsSection && reportHasLoadedOnceRef.current && updateDataTable !== false) {
      setLoading(true);
    }
  }, [isRecordsSection, setLoading, updateDataTable]);

  const downloadFilteredSignatures = async (rows = []) => {
    if (!canEditReports || isDownloadingSignatureArchive) return;

    const checkinIds = Array.from(new Set(
      (Array.isArray(rows) ? rows : [])
        .filter((row) => Number(row?.has_signature) === 1)
        .map((row) => row?.checkin_record_id || row?.checkin_id)
        .map((id) => Number(id))
        .filter((id) => Number.isSafeInteger(id) && id > 0)
    ));

    if (checkinIds.length === 0) {
      notyf.open({ type: "danger", message: "Nenhuma assinatura encontrada nos registros filtrados.", ripple: true, dismissible: true });
      return;
    }

    setIsDownloadingSignatureArchive(true);
    const toastId = notyf.open({ type: "success", message: "Gerando o ZIP das assinaturas...", ripple: true, dismissible: false, duration: 0 });

    try {
      const response = await axios.post(
        `/api/upload/plataforma-eventos/${dataTable.evento_id}/signatures/archive`,
        { checkin_ids: checkinIds },
        { responseType: "blob" },
      );
      const downloadUrl = window.URL.createObjectURL(new Blob([response.data], { type: "application/zip" }));
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = getDownloadFileName(response.headers?.["content-disposition"], `evento-${dataTable.evento_id}-assinaturas.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      notyf.dismiss(toastId);
      notyf.open({ type: "success", message: "Download das assinaturas concluído.", ripple: true, dismissible: true });
    } catch (error) {
      let message = "Não foi possível gerar o ZIP das assinaturas.";
      if (error.response?.data instanceof Blob) {
        try {
          const body = JSON.parse(await error.response.data.text());
          message = body.message || message;
        } catch (parseError) {
          // Keep the generic message when the response is not JSON.
        }
      } else {
        message = error.response?.data?.message || message;
      }
      notyf.dismiss(toastId);
      notyf.open({ type: "danger", message, ripple: true, dismissible: true });
    } finally {
      setIsDownloadingSignatureArchive(false);
    }
  };

  function handleExportResult(props) {
    setLoading(true);
    axios
      .get(
        `/api/event/plataformaEventos/collectedParticipants/${props.user_id}?event_id=${dataTable.event_id}&user_type=${props.type}`,
      )
      .then(function (response) {
        let data = Array.isArray(response.data) ? response.data : [];
        if (data.length > 0) {
          const headers = getCollectedParticipantsExportHeaders(dataTable.json?.form?.fields, {
            includeHiddenFields: true,
          });
          Promise.resolve(getExcelFromObject({
            filename: dataTable.titulo + " - " + props.email,
            headers,
            data,
          })).catch(function () {
            notyf.open({
              type: "danger",
              message: "Não foi possível gerar o arquivo de exportação",
              ripple: true,
              dismissible: true,
            });
          });
        } else {
          notyf.open({
            type: "danger",
            message: "Nenhum participante coletado",
            ripple: true,
            dismissible: true,
          });
        }
        setLoading(false);
      })
      .catch(function (error) {
        setLoading(false);
        notyf.open({
          type: "danger",
          message: "Houve um erro, tente novamente",
          ripple: true,
          dismissible: true,
        });
      });
    // let headers = []
  }


  function handleOpenCollectedParticipantsModal(props) {
    setSelectedUser(props);
    setShowCollectedParticipantsModal({ show: true });
  }


  const validateDate = (dateString, fieldName) => {
    if (!dateString) return { isValid: false, error: "Data é obrigatória" };
    try {
      const parsedDate = typeof dateString === "string"
        ? parse(dateString, "dd/MM/yyyy HH:mm", new Date())
        : dateString;
      if (!isValid(parsedDate)) return { isValid: false, error: "Formato de data inválido" };
      return { isValid: true, error: null };
    } catch {
      return { isValid: false, error: "Formato de data inválido" };
    }
  };

  const handleDateRangeChange = (type, date, filterType = "participants") => {
    if (filterType === "credentialCharts") {
      setFilterCredentialCharts((prev) => ({
        ...prev,
        dateRange: {
          ...prev.dateRange,
          [type]: date,
        },
      }));
      return;
    }

    if (filterType === "participantCheckins") {
      setFilterCheckins((prev) => ({
        ...prev,
        checkinDateRange: {
          ...prev.checkinDateRange,
          [type]: date,
        },
      }));
      return;
    }

    const setter = filterType === "participants" ? setFilterParticipants : setFilterUsers;
    setter((prev) => ({
      ...prev,
      dateRange: {
        ...prev.dateRange,
        [type]: date,
      },
    }));
  };

  function handleCheckin(participant) {
    if (!canEditReports) {
      return;
    }

    confirmAlert({
      closeOnEscape: false,
      closeOnClickOutside: false,
      customUI: ({ onClose }) => (
        <ShowConfirm
          title="Confirmar ação"
          description="Registrar check-in desse participante?"
          action={() => {
            setLoading(true);
            axios
              .post(`/api/checkin/${dataTable.evento_id}`, {
                user_id: participant.user_id,
                origin: "Painel",
                ip: "",
                location: "",
              })
              .then(() => {
                notyf.open({
                  type: "success",
                  message: "Check-in realizado com sucesso!",
                  ripple: true,
                  dismissible: true,
                });
                setDataTableUpdate((prevCount) => prevCount + 1);
              })
              .catch(() => {
                notyf.open({
                  type: "danger",
                  message: "Houve um erro, tente novamente",
                  ripple: true,
                  dismissible: true,
                });
              })
              .finally(() => setLoading(false));
            onClose();
          }}
          onClose={onClose}
        />
      ),
    });
  }

  function handleDelete(props, text) {
    if (!canEditReports) {
      return;
    }

    confirmAlert({
      closeOnEscape: false,
      closeOnClickOutside: false,
      customUI: ({ onClose }) => {
        return (
          <ShowConfirm
            title="Confirmar ação"
            description={text}
            action={() => {
              deleteHistory(props);
              onClose();
            }}
            onClose={onClose}
          />
        );
      },
    });
  }

  function deleteHistory(props) {
    if (!canEditReports) {
      return;
    }

    setLoading(true);
    let url = "";
    switch (props.type) {
      case "removeUsersMod":
      case "removeUsers":
        url = "/api/event/participants";
        break;
      case "removeUsersHistory":
      case "removeUsersModHistory":
        url = "/api/event/participants/history";
        break;
      case "removeUsersResults":
        url = "/api/event/result";
        break;
      case "resetCheckins":
      case "removeCheckin":
        url = "/api/event/checkin";
        break;
      case "resetScoreHistory":
        url = "/api/event/plataformaEventos/participant-score-history";
        break;
      case "resetAnalytics":
        url = `/api/event/plataformaEventos/analytics-dashboard/${props.evento_id}`;
        break;

      default:
        setLoading(false);
        notyf.open({
          type: "danger",
          message: "Houve um erro, tente novamente",
          ripple: true,
          dismissible: true,
        });
        return;
    }
    axios
      .delete(url, { params: props })
      .then(function (response) {
        if (props.type === "removeUsers") {
          if (props.id || props.user_id) {
            setLocalTotalParticipantes((prev) => Math.max(0, prev - 1));
          } else {
            setLocalTotalParticipantes(0);
          }
        }
        notyf.open({
          type: "success",
          message: "Exclusão realizada com sucesso",
          ripple: true,
          dismissible: true,
        });
        setDataTableUpdate((prevCount) => prevCount + 1);
      })
      .catch(function (error) {
        setLoading(false);
        const deletedCount = Number(error.response?.data?.deleted_count || 0);
        if (deletedCount > 0) {
          if (props.type === "removeUsers") {
            setLocalTotalParticipantes((prev) => Math.max(0, prev - deletedCount));
          }
          setDataTableUpdate((prevCount) => prevCount + 1);
        }
        notyf.open({
          type: "danger",
          message: error.response?.data?.message || "Houve um erro, tente novamente",
          ripple: true,
          dismissible: true,
        });
      });
  }

  const submitFormExternally = () => {
    if (canEditReports && formikRef.current) {
      formikRef.current.submitForm();
    }
  };

  const handleEdit = (data, type) => {
    if (!canEditReports) {
      return;
    }

    setShowEditModal({ show: true, type: type, data: data });
  };

  const handleNewUser = (type) => {
    if (!canEditReports) {
      return;
    }

    setShowEditModal({ show: true, type: type, data: {} });
  };

  useEffect(() => {
    if (!showEditModal.show || showEditModal.type !== 1) {
      setAvailableExhibitors([]);
      setLoadingAvailableExhibitors(false);
      return;
    }

    let ignore = false;
    setLoadingAvailableExhibitors(true);

    axios
      .get("/api/event/plataformaEventos/available-exhibitors", {
        params: {
          event_id: dataTable.evento_id,
          user_id: showEditModal.data.user_id || undefined,
        },
      })
      .then(function (response) {
        if (!ignore) {
          setAvailableExhibitors(response.data.exhibitors || []);
        }
      })
      .catch(function (error) {
        if (!ignore) {
          setAvailableExhibitors([]);
          notyf.open({
            type: "danger",
            message:
              error?.response?.data?.message ||
              "Houve um erro ao carregar os expositores disponíveis",
            ripple: true,
            dismissible: true,
          });
        }
      })
      .finally(function () {
        if (!ignore) {
          setLoadingAvailableExhibitors(false);
        }
      });

    return () => {
      ignore = true;
    };
  }, [
    showEditModal.show,
    showEditModal.type,
    showEditModal.data.user_id,
    dataTable.evento_id,
    notyf,
  ]);

  function registerUser(values, actions, userType, showFullForm) {
    if (!canEditReports) {
      actions?.setSubmitting(false);
      return;
    }

    let newArray = { ...values };
    let email = null;
    let user_id = null;
    let type = 0;
    let status = 0;
    let exhibitor_id = null;
    if (newArray) {
      if (newArray.hasOwnProperty("authorization")) {
        delete newArray.authorization;
      }
      if (newArray.email) {
        email = newArray.email;
      }
      if (newArray.user_id) {
        user_id = newArray.user_id;
      }
      if (
        newArray.type !== undefined &&
        newArray.type !== null &&
        newArray.type !== ""
      ) {
        type = Number(newArray.type);
      }
      if (
        newArray.exhibitor_id !== undefined &&
        newArray.exhibitor_id !== null &&
        newArray.exhibitor_id !== ""
      ) {
        exhibitor_id = Number(newArray.exhibitor_id);
      }
      delete newArray.email;
      delete newArray.exhibitor_id;
      delete newArray.user_id;
      delete newArray.type;
      delete newArray.status;
      if (type !== 2) {
        exhibitor_id = null;
      }
      if (showFullForm) {
        status = 1;
      }
    }
    const formFields = userType > 0
      ? dataTable.json.form.fieldsUsers
      : dataTable.json.form.fields;
    const json = JSON.stringify(buildParticipantJson(
      showFullForm ? newArray : {},
      formFields,
      user_id ? showEditModal.data : null,
    ));
    if (user_id) {
      runWithGlobalLoading(setLoading, () =>
        axios.put(`/api/event/participants/${dataTable.evento_id}`, {
          email: email,
          exhibitor_id: exhibitor_id,
          user_id: user_id,
          platform: dataTable.platform,
          status: status,
          type: type,
          json: json,
        }),
      )
        .then(function (response) {
          setShowEditModal((prevState) => ({
            ...prevState,
            show: false,
            type: 0,
          }));
          setDataTableUpdate((prevCount) => prevCount + 1);
          actions.setSubmitting(false);
        })
        .catch(function (error) {
          actions.setSubmitting(false);
          notyf.open({
            type: "danger",
            message:
              getFormFieldVacancyErrorMessage(error) ||
              error?.response?.data?.message ||
              `Houve um erro no registro do ${userType > 0 ? "usuário" : "participante"}`,
            ripple: true,
            dismissible: true,
          });
        });
    } else {
      const executeRegister = () => {
        runWithGlobalLoading(setLoading, () =>
          axios.post("/api/event/participants", {
            evento_id: dataTable.evento_id,
            email: email,
            exhibitor_id: exhibitor_id,
            type: type,
            platform: dataTable.platform,
            sendEmail: dataTable.json.email.register.status,
            status: status,
            json: json,
          }),
        )
          .then(function (response) {
            if (!userType || userType === 0) {
              setLocalTotalParticipantes((prev) => prev + 1);
            }
            setShowEditModal((prevState) => ({
              ...prevState,
              show: false,
              type: 0,
            }));
            setDataTableUpdate((prevCount) => prevCount + 1);
            actions.setSubmitting(false);
          })
          .catch(function (error) {
            actions.setSubmitting(false);
            let errorMessage =
              getFormFieldVacancyErrorMessage(error) ||
              (error?.response?.data?.code === 3
                ? "Este e-mail já está registrado"
                : error?.response?.data?.message ||
                `Houve um erro no registro do ${userType > 0 ? "usuário" : "participante"}`);
            notyf.open({
              type: "danger",
              message: errorMessage,
              ripple: true,
              dismissible: true,
            });
          });
      };

      const formStatus = dataTable.json?.form?.status;
      const subscriptionLimitStatus = dataTable.json?.subscriptionLimit?.status;
      const subscriptionLimit = dataTable.json?.subscriptionLimit?.limit;
      const currentParticipantsCount = historyData?.totalParticipants || historyData?.participants?.length || dataTable.total_participantes;

      console.log("--- DEBUG subscriptionLimit (EventHistory02) ---");
      console.log("dataTable.json?.subscriptionLimit:", dataTable.json?.subscriptionLimit);
      console.log("subscriptionLimitStatus:", subscriptionLimitStatus);
      console.log("subscriptionLimit:", subscriptionLimit);
      console.log("currentParticipantsCount:", currentParticipantsCount);
      console.log("userType:", userType);

      const proceedWithLimitCheck = () => {
        if (
          (!userType || userType === 0) &&
          subscriptionLimitStatus &&
          subscriptionLimit &&
          subscriptionLimit > 0 &&
          currentParticipantsCount >= subscriptionLimit
        ) {
          confirmAlert({
            closeOnEscape: false,
            closeOnClickOutside: false,
            customUI: ({ onClose }) => {
              return (
                <ShowConfirm
                  title="Confirmar ação"
                  description={`O limite de inscrições (${subscriptionLimit}) foi atingido. Deseja registrar mesmo assim?`}
                  action={() => {
                    executeRegister();
                    onClose();
                  }}
                  onClose={() => {
                    actions.setSubmitting(false);
                    setLoading(false);
                    onClose();
                  }}
                />
              );
            },
          });
        } else {
          executeRegister();
        }
      };

      if ((!userType || userType === 0) && formStatus !== undefined && formStatus !== true && formStatus !== 'true') {
        confirmAlert({
          closeOnEscape: false,
          closeOnClickOutside: false,
          customUI: ({ onClose }) => {
            return (
              <ShowConfirm
                title="Confirmar ação"
                description="As inscrições para este evento estão fechadas. Deseja registrar o participante mesmo assim?"
                action={() => {
                  proceedWithLimitCheck();
                  onClose();
                }}
                onClose={() => {
                  actions.setSubmitting(false);
                  setLoading(false);
                  onClose();
                }}
              />
            );
          },
        });
      } else {
        proceedWithLimitCheck();
      }
    }
  }

  const participantActionsRef = useRef(null);
  participantActionsRef.current = { handleDelete, handleEdit, handleCheckin, handleOpenCollectedParticipantsModal };
  const participantAction = useCallback((name) => (...args) => participantActionsRef.current[name](...args), []);
  const showParticipantExtra = filterParticipants.extraPropsParticipants;
  const showCheckinExtra = filterCheckins.extraPropsParticipants;
  const checkinGrouping = filterCheckins.checkin_filter;
  const registrationWindowMinutes = filterCheckins.registrationWindowMinutes;
  const showUserExtra = filterUsers.extraPropsUsers;
  useEffect(() => {
    if (!isRecordsSection) return;
    const participantFilter = { participantReportMode: "records", extraPropsParticipants: showParticipantExtra };
    setHeaders02(dataTable, setFields, participantAction("handleDelete"), participantAction("handleEdit"), participantFilter, setShowImageModal, canEditReports, participantAction("handleCheckin"), setFilterParticipants);
  }, [isRecordsSection, dataTable, canEditReports, showParticipantExtra, participantAction]);

  useEffect(() => {
    if (!isRecordsSection) return;
    const checkinFilter = { participantReportMode: "checkins", extraPropsParticipants: showCheckinExtra, checkin_filter: checkinGrouping, registrationWindowMinutes };
    setHeaders02(dataTable, setFieldsCheckins, participantAction("handleDelete"), participantAction("handleEdit"), checkinFilter, setShowImageModal, canEditReports, participantAction("handleCheckin"), setFilterCheckins);
  }, [isRecordsSection, dataTable, canEditReports, showCheckinExtra, checkinGrouping, registrationWindowMinutes, participantAction]);

  useEffect(() => {
    if (!isRecordsSection) return;
    setHeaders02(dataTable, setFieldsRsvp, participantAction("handleDelete"), participantAction("handleEdit"), { participantReportMode: "records", extraPropsParticipants: true }, setShowImageModal, canEditReports, participantAction("handleCheckin"));
  }, [isRecordsSection, dataTable, canEditReports, participantAction]);

  useEffect(() => {
    if (!isRecordsSection) return;
    setHeadersUsers(dataTable, setFieldsUsers, participantAction("handleDelete"), participantAction("handleEdit"), participantAction("handleOpenCollectedParticipantsModal"), { extraPropsUsers: showUserExtra }, setShowImageModal, canEditReports);
  }, [isRecordsSection, dataTable, canEditReports, showUserExtra, participantAction]);

  useEffect(() => {
    if (!isRecordsSection) return;
    const controller = new AbortController();
    const requestId = ++requestSequenceRef.current;
    setRecordsError("");
    setLoading(true);
    axios.get(`/api/event/participants/${dataTable.evento_id}?platform=${dataTable.platform}&includeCheckins=true`, { signal: controller.signal })
      .then((response) => {
        if (controller.signal.aborted || requestId !== requestSequenceRef.current) return;
        setParticipantsResponse({ eventId: dataTable.evento_id, data: response.data });
        if (updateDataTableWarning === "update") {
          setDataTableUpdateWarning(false);
          notyf.open({ type: "success", message: "Dados atualizados com sucesso", ripple: true, dismissible: true });
        }
      })
      .catch(() => {
        if (controller.signal.aborted || requestId !== requestSequenceRef.current) return;
        setRecordsError("Não foi possível carregar os registros do evento.");
      })
      .finally(() => { if (!controller.signal.aborted && requestId === requestSequenceRef.current) setLoading(false); });
    return () => { controller.abort(); if (requestId === requestSequenceRef.current) setLoading(false); };
  }, [isRecordsSection, dataTable.evento_id, dataTable.platform, updateDataTable, resetTrigger, setLoading, notyf, updateDataTableWarning]);

  return (
    <Container fluid className="p-0">
      <MediaPreviewModal media={showImageModal} setMedia={setShowImageModal} />
      <ModalEdit02
        showEditModal={showEditModal}
        setShowEditModal={setShowEditModal}
        availableExhibitors={availableExhibitors}
        dataApplication={dataTable}
        formikRef={formikRef}
        loadingAvailableExhibitors={loadingAvailableExhibitors}
        registerUser={registerUser}
        submitFormExternally={submitFormExternally}
        canEditReports={canEditReports}
        setShowImageModal={setShowImageModal}
      ></ModalEdit02>
      <ModalCollectedParticipants
        showModal={showCollectedParticipantsModal}
        setShowModal={setShowCollectedParticipantsModal}
        selectedUser={selectedUser}
        dataTable={dataTable}
        handleExportResult={handleExportResult}
      ></ModalCollectedParticipants>

      {isRecordsSection && recordsError && <Alert variant="danger" className="d-flex justify-content-between align-items-center gap-2 flex-wrap">
        <span>{recordsError}</span><Button size="sm" variant="outline-danger" onClick={() => setResetTrigger((value) => value + 1)}>Tentar novamente</Button>
      </Alert>}
      {isRecordsSection && !historyData && !recordsError && <div className="py-5"><Loader /></div>}
      {isRecordsSection && historyData && <>
        <EventRecordsSummarySection02 historyData={historyData} dataTable={dataTable}
          refreshKey={JSON.stringify([updateDataTable, resetTrigger])}
          onViewParticipants={() => scrollToRecordTable(participantTableRef.current)}
          onViewRsvpTable={() => scrollToRecordTable(rsvpTableRef.current || rsvpSectionRef.current)} />
        <ParticipantsSection02 historyData={historyData} triggerExport={triggerExport} setTriggerExport={setTriggerExport}
          zipIsReady={zipIsReady} setZipIsReady={setZipIsReady} filteredParticipants={filteredParticipants} dataTable={dataTable} notyf={notyf}
          filter={filterParticipants} setFilter={setFilterParticipants} handleDelete={handleDelete} handleNewUser={handleNewUser} handleDateRangeChange={handleDateRangeChange}
          getDefaultDateRangeMonth={getDefaultDateRangeMonth} getDefaultCheckinDateRange={getDefaultCheckinDateRange} fields={fields} setFilteredParticipants={setFilteredParticipants}
          canEdit={canEditReports} resetTrigger={resetTrigger} tableRef={participantTableRef}>
          <ChartSection02 entries={chartEntries} dataTable={dataTable} showHeader={false} />
        </ParticipantsSection02>
        <ParticipantsSection02 mode="checkins" historyData={historyData} triggerExport={triggerExportCheckins} setTriggerExport={setTriggerExportCheckins}
          zipIsReady={checkinsZipIsReady} setZipIsReady={setCheckinsZipIsReady} filteredParticipants={filteredCheckins} dataTable={dataTable} notyf={notyf}
          filter={filterCheckins} setFilter={setFilterCheckins} handleDelete={handleDelete} handleNewUser={handleNewUser} handleDateRangeChange={handleDateRangeChange}
          getDefaultDateRangeMonth={getDefaultDateRangeMonth} getDefaultCheckinDateRange={getDefaultCheckinDateRange} fields={fieldsCheckins} setFilteredParticipants={setFilteredCheckins}
          canEdit={canEditReports} resetTrigger={resetTrigger} signatureEnabled={signatureEnabled}
          downloadFilteredSignatures={downloadFilteredSignatures} isDownloadingSignatureArchive={isDownloadingSignatureArchive}>
          <CheckinsSection02 historyData={historyData} dataTable={dataTable} filterCredentialCharts={filterCredentialCharts} setFilterCredentialCharts={setFilterCredentialCharts}
            handleDateRangeChange={handleDateRangeChange} getDefaultDateRangeMonth={getDefaultDateRangeMonth} setResetTrigger={setResetTrigger}
            handleDelete={handleDelete} canEdit={canEditReports} showHeader={false} />
        </ParticipantsSection02>
        <RsvpRecordsSection02 historyData={historyData} dataTable={dataTable} fields={fieldsRsvp} sectionRef={rsvpSectionRef} tableRef={rsvpTableRef}
          canManageRsvps={canAccessEventRsvps(userState, dataTable.platform)}
          refreshKey={JSON.stringify([updateDataTable, resetTrigger])} onResponsesChanged={() => setResetTrigger((value) => value + 1)} />

          {/* Lista de usuários */}
          <UsersSection02
            historyData={historyData}
            triggerExportUsers={triggerExportUsers}
            setTriggerExportUsers={setTriggerExportUsers}
            zipIsReady={zipIsReady}
            setZipIsReady={setZipIsReady}
            filteredUsers={filteredUsers}
            dataTable={dataTable}
            notyf={notyf}
            filter={filterUsers}
            setFilter={setFilterUsers}
            handleDelete={handleDelete}
            handleNewUser={handleNewUser}
            handleDateRangeChange={handleDateRangeChange}
            getDefaultDateRangeMonth={getDefaultDateRangeMonth}
            fieldsUsers={fieldsUsers}
            setFilteredUsers={setFilteredUsers}
            canEdit={canEditReports}
            resetTrigger={resetTrigger}
            setResetTrigger={setResetTrigger}
          />

      </>}

      {!isRecordsSection && (
        <>
          {!reportsReady && <div className="py-5"><Loader /></div>}
          <div className={!reportsReady ? "d-none" : ""}>
          <EventAnalyticsDashboard
            dataTable={dataTable} handleDelete={handleDelete} canReset={canManageAnalytics} canRunUsageTest={canManageAnalytics}
            refreshTrigger={updateDataTable} onLoadingChange={handleReportLoadingChange} />
          <StreamingReportsSection dataTable={dataTable} refreshTrigger={updateDataTable}
            setDataTableUpdate={setDataTableUpdate} onLoadingChange={handleReportLoadingChange} />
          <EventParticipantRankingSection key={dataTable.evento_id} dataTable={dataTable} canEdit={canEditReports}
            refreshTrigger={updateDataTable} onLoadingChange={handleReportLoadingChange} />
          <ActivityReportsSection dataTable={dataTable} canEdit={canEditReports}
            refreshTrigger={updateDataTable} onLoadingChange={handleReportLoadingChange} />
          </div>
        </>
      )}
    </Container>
  );
};


const ModalEdit01 = ({
  showEditModal,
  setShowEditModal,
  dataApplication,
  formikRef,
  registerUser,
  submitFormExternally,
}) => {
  return (
    <Modal
      show={showEditModal.show}
      backdrop="static"
      keyboard={false}
      centered
      fullscreen={"xl-down"}
      onHide={() =>
        setShowEditModal((prevState) => ({
          ...prevState,
          show: false,
          type: "participant",
        }))
      }
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {showEditModal.data.visitante_id
            ? "Editar participante"
            : "Adicionar participante"}
          {showEditModal.data.visitante_id && (
            <>
              <br></br>
              <small>
                ID:{" "}
                <span className="opacity-75">
                  {showEditModal.data.visitante_id}
                </span>
              </small>
            </>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {dataApplication.json.form.fields.length === 0 ? (
          <div className="text-center">
            <p className="my-3">Nenhum campo de formulário encontrado</p>
          </div>
        ) : (
          <>
            <FormComponent
              dataApplication={dataApplication}
              formikRef={formikRef}
              registerUser={registerUser}
              editValues={
                showEditModal.data.visitante_id ? showEditModal.data : false
              }
              type={showEditModal.type}
              showFullFormStatus={true}
            ></FormComponent>

            {showEditModal.data.visitante_id && (
              <UserAdditionalInfoSection
                platform={1}
                data={showEditModal.data}
              />
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <div className="text-center">
          <Button variant="primary" onClick={submitFormExternally}>
            Salvar
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

const CredentialParticipantInfoSection = ({ participant, dataTable, canDownloadSignature, setShowImageModal }) => {
  const checkinHistory = participant.checkinHistory || [];
  const signatureEnabled = dataTable?.json?.credentialAccess?.apresentation?.afterCheckin?.signature?.status === true;

  return (
    <div className="mt-3 pt-3 border-top">
      <h6 className="mb-3">Credenciamento</h6>
      <p className="mb-3"><span className="fw-bold">Origem de registro</span><br />
        <Badge bg="primary" className="text-black">
          {participant.origin ? getOriginLabel(participant.origin) : "(Não identificado)"}
        </Badge>
      </p>
      <p className="fw-bold mb-2">Histórico de check-ins</p>
      {checkinHistory.length === 0 ? (
        <span className="text-muted small">Nenhum check-in registrado.</span>
      ) : checkinHistory.map((checkin, index) => (
        <Card key={`${checkin.id || checkin.data_checkin || index}`} className="mb-2"><Card.Body className="p-3">
          <p className="mb-2 fw-bold">{checkin.data_checkin_formatted || "(Data não informada)"}</p>
          <div className="d-flex flex-wrap gap-2">
            <Badge bg="primary">{checkin.checkin_origin || "(Não identificado)"}</Badge>
            <Badge bg="secondary">{checkin.checkin_location || "Localização não identificada"}</Badge>
            {checkin.checkin_ip && <Badge bg="secondary">IP: {checkin.checkin_ip}</Badge>}
          </div>
          {signatureEnabled && (
            <div className="mt-3 pt-3 border-top">
              <div>
                <span className="fw-bold d-block">Assinatura</span>
                <div className="d-flex flex-column align-items-start gap-2">
                  {Number(checkin.has_signature) === 1 && (
                    <Badge
                      as={canDownloadSignature && checkin.signature_file_name ? "a" : "span"}
                      {...(canDownloadSignature && checkin.signature_file_name ? {
                        href: getSignatureFileUrl(dataTable, checkin.signature_file_name),
                        target: "_blank",
                        rel: "noopener noreferrer",
                        className: "text-decoration-none",
                      } : {})}
                      bg="success"
                    >
                  {/*
                  {Number(checkin.has_signature) === 1 ? "Registrada" : "Não registrada"}
                  */}
                      Registrada
                    </Badge>
                  )}
                  {Number(checkin.has_signature) === 1 && canDownloadSignature && checkin.signature_file_name && (
                    <div className="d-flex gap-2">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline-primary"
                        onClick={() => setShowImageModal({
                          show: true,
                          url: getSignatureFileUrl(dataTable, checkin.signature_file_name),
                          id: checkin.id,
                        })}
                      >
                        Visualizar assinatura
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="primary"
                        onClick={() => downloadSignatureFile(dataTable, checkin.signature_file_name)}
                      >
                        Baixar assinatura
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </Card.Body></Card>
      ))}
    </div>
  );
};

const ModalEdit02 = ({
  showEditModal,
  setShowEditModal,
  availableExhibitors,
  dataApplication,
  formikRef,
  loadingAvailableExhibitors,
  registerUser,
  submitFormExternally,
  canEditReports,
  setShowImageModal,
}) => {
  return (
    <Modal
      show={showEditModal.show}
      backdrop="static"
      keyboard={false}
      centered
      fullscreen={"xl-down"}
      onHide={() =>
        setShowEditModal((prevState) => ({
          ...prevState,
          show: false,
          type: "participant",
        }))
      }
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {showEditModal.type === 1
            ? showEditModal.data.user_id
              ? "Editar usuário"
              : "Adicionar usuário"
            : showEditModal.data.user_id
              ? "Editar participante"
              : "Adicionar participante"}
          {showEditModal.data.user_id && (
            <>
              <br></br>
              <small>
                ID:{" "}
                <span className="opacity-75">{showEditModal.data.user_id}</span>
              </small>
            </>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <FormComponent
          availableExhibitors={availableExhibitors}
          dataApplication={dataApplication}
          formikRef={formikRef}
          loadingAvailableExhibitors={loadingAvailableExhibitors}
          registerUser={registerUser}
          editValues={showEditModal.data.user_id ? showEditModal.data : false}
          type={showEditModal.type}
          showFullFormStatus={showEditModal.data.user_id ? true : false}
        ></FormComponent>

        {showEditModal.data.user_id && showEditModal.type === 0 && (
          <CredentialParticipantInfoSection
            participant={showEditModal.data}
            dataTable={dataApplication}
            canDownloadSignature={canEditReports}
            setShowImageModal={setShowImageModal}
          />
        )}

        {showEditModal.data.user_id && (
          <UserAdditionalInfoSection
            platform={2}
            data={showEditModal.data}
            viewType={showEditModal.type}
          />
        )}
      </Modal.Body>
      <Modal.Footer>
        <div className="text-center">
          <Button variant="primary" onClick={submitFormExternally}>
            Salvar
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};
